import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Input, Textarea } from '../FormField';
import SearchableSelect from '../ui/SearchableSelect';
import type { Client } from '../../services/ClientService';
import type { ProjectFormErrors, ProjectFormValues } from '../../utils/projectForm';
import { NEW_CLIENT_PATH } from '../../utils/accountRoutes';

const NO_CLIENT = '';

interface ProjectDetailsFormProps {
  values: ProjectFormValues;
  /** Message for a field, from local checks or the server; undefined when fine. */
  errorFor: (key: keyof ProjectFormErrors) => string | undefined;
  /** Change fields; the keys after the patch are the API fields whose error to drop. */
  onChange: (patch: Partial<ProjectFormValues>, ...cleared: (keyof ProjectFormErrors)[]) => void;
  clients: Client[];
  /** Shown for the current client while the client list does not contain it (archived, or the list failed). */
  clientName: string;
  disabled?: boolean;
}

/**
 * The Details fields of a project, shared by every screen that edits one.
 * Holds no state of its own: the page owns the values, validation and saving.
 */
const ProjectDetailsForm: React.FC<ProjectDetailsFormProps> = ({ values, errorFor, onChange, clients, clientName, disabled = false }) => {
  const clientOptions = useMemo(() => {
    const opts = [{ value: NO_CLIENT, label: 'No client' }, ...clients.map(c => ({ value: c.id, label: c.name }))];
    if (values.clientId && !opts.some(o => o.value === values.clientId)) {
      opts.push({ value: values.clientId, label: clientName || 'Current client' });
    }
    return opts;
  }, [clients, values.clientId, clientName]);

  return (
    <>
      <Input label="Name" required value={values.name} error={errorFor('name')} disabled={disabled}
        onChange={e => onChange({ name: e.target.value }, 'name')} autoComplete="off" />
      <Textarea label="Description" value={values.description} error={errorFor('description')} disabled={disabled}
        onChange={e => onChange({ description: e.target.value }, 'description')} rows={3} />
      <div className="form-group">
        <span className="form-label" id="epp-client-label">Client</span>
        <SearchableSelect
          value={values.clientId}
          onChange={v => onChange({ clientId: v }, 'client_id')}
          options={clientOptions}
          placeholder="No client"
          ariaLabel="Client"
          error={errorFor('client_id')}
          disabled={disabled}
        />
        {errorFor('client_id') && <span className="form-error-text">{errorFor('client_id')}</span>}
        <Link className="epp-link" to={NEW_CLIENT_PATH}>Create a client</Link>
      </div>
      <div className="epp-row">
        <Input label="Start date" type="date" value={values.dateStart} error={errorFor('date_start')} disabled={disabled}
          onChange={e => onChange({ dateStart: e.target.value }, 'date_start', 'date_end')} />
        <Input label="End date" type="date" value={values.dateEnd} error={errorFor('date_end')} disabled={disabled}
          onChange={e => onChange({ dateEnd: e.target.value }, 'date_end')} />
      </div>
      <Input label="Event date" type="date" value={values.eventDate} error={errorFor('event_date')} disabled={disabled}
        onChange={e => onChange({ eventDate: e.target.value }, 'event_date')} />
      <div className="epp-row">
        <Input label="City" value={values.city} error={errorFor('location_city')} autoComplete="address-level2" disabled={disabled}
          onChange={e => onChange({ city: e.target.value }, 'location_city')} />
        <Input label="Country" value={values.country} error={errorFor('location_country')} autoComplete="country-name" disabled={disabled}
          onChange={e => onChange({ country: e.target.value }, 'location_country')} />
      </div>
    </>
  );
};

export default ProjectDetailsForm;
